import { trackRSProductClicked } from '@app/analytics';
import { StockStatus } from '@app/data/ihs';
import { Box, MediaCard } from '@redbubble/design-system';
import { useIntl } from '@redbubble/next/intl';
import {
  AnalyticsItemListID,
  AnalyticsStorage,
  AnalyticsStorageKeys,
} from '@redbubble/next-analytics/gtm';
import {
  type RSPageTypeKey,
  type RSProductClickedLocationKey,
  useRudderstackClient,
} from '@redbubble/next-provider/rudderstack';
import Image from 'next/image';
import React, { ReactNode, useCallback } from 'react';
import { usePrefersReducedMotion } from '../../pages/i/components/ProductConfigurator/usePrefersReducedMotion';
import searchUUIDStorage from '../../utils/searchUUIDStorage';
import { EstablishedArtistCaption } from '../EstablishedArtistCaption';
import {
  MatureContentCover,
  MatureContentCoverStyle,
} from '../MatureContentCover';
import { messages } from './messages';
import styles from './ProductCard.module.scss';
import { SEOTagsCover } from './SEOTagsCover/SEOTagsCover';

export interface ProductCardProps {
  children?: ReactNode;
  title?: string;
  workTitle: string;
  link?: string;
  artistName?: string;
  imageSrc?: string | null;
  imagePriority?: boolean;
  caption?: string;
  matureContent: boolean;
  loading?: boolean;
  testId?: string;
  tags?: string[];
  artistIsEstablished?: boolean;
  zoomOnHover?: boolean;
  primaryAction?: ReactNode;
  onMouseDown?: (event: React.MouseEvent) => void;
  onClick?: (event?: MouseEvent) => void;
  analyticsComponentID: AnalyticsItemListID;
  analyticsIndex?: number;
  searchUUID?: string | null;
  disableSeoTag?: boolean | null;
  hideLink?: boolean;
  stockStatus?: StockStatus;
  rsLocation?: RSProductClickedLocationKey;
  rsPageType?: RSPageTypeKey;
}

const storeAnalyticsData = (
  analyticsComponentID: AnalyticsItemListID,
  analyticsIndex?: number
) => {
  AnalyticsStorage.setValue(
    AnalyticsStorageKeys.RefListID,
    analyticsComponentID
  );
  if (Number.isInteger(analyticsIndex))
    AnalyticsStorage.setValue(
      AnalyticsStorageKeys.RefListIndex,
      `${analyticsIndex}`
    );
};

export const ProductCard: React.FC<ProductCardProps> = ({
  children,
  title,
  workTitle,
  link,
  artistName,
  imageSrc,
  imagePriority,
  caption,
  matureContent,
  loading,
  testId,
  tags,
  artistIsEstablished,
  zoomOnHover,
  primaryAction,
  onMouseDown,
  onClick,
  analyticsComponentID,
  analyticsIndex,
  searchUUID,
  disableSeoTag,
  hideLink,
  rsLocation,
  rsPageType,
}) => {
  const { RudderstackClient } = useRudderstackClient();
  const intl = useIntl();
  const prefersReducedMotion = usePrefersReducedMotion();

  const mouseDownEvent = (event: React.MouseEvent) => {
    storeAnalyticsData(analyticsComponentID, analyticsIndex);
    if (onMouseDown) onMouseDown(event);
    if (link && searchUUID) searchUUIDStorage.set(link, searchUUID);
  };

  const altText =
    workTitle && artistName
      ? intl.formatMessage(messages.previewAltText, {
          workTitle,
          artistName,
        })
      : intl.formatMessage(messages.previewAltTextFallback);

  const cardCaption = artistIsEstablished ? (
    <EstablishedArtistCaption caption={caption} />
  ) : (
    caption
  );

  const ImageElement = useCallback(() => {
    if (!imageSrc) return null;

    return (
      /**
       * TODO: Refactor to use :has() css psuedo-class when
       * sufficiently supported (https://caniuse.com/?search=has)
       * so we don't have to pass matureContent to SEOTagsCover
       */
      <SEOTagsCover
        tags={tags}
        matureContent={matureContent}
        disableSeoTag={disableSeoTag}
      >
        <MatureContentCover
          matureContent={matureContent}
          aspectRatioHeight={1}
          styleType={MatureContentCoverStyle.MediaCard}
        >
          <Box display="flex">
            <Image
              className={[
                styles.productCardImage,
                !prefersReducedMotion && zoomOnHover ? styles.imageHover : '',
              ].join(' ')}
              src={imageSrc}
              alt={altText}
              height={600}
              width={600}
              // TODO: Remove after react upgrade, check tests pass
              fetchPriority={imagePriority ? 'high' : undefined}
              priority={imagePriority}
              placeholder="blur"
              // this is a random base64 string that is a gray transparent image
              blurDataURL="data:image/gif;base64,R0lGODlhAQABAIAAAMLCwgAAACH5BAAAAAAALAAAAAABAAEAAAICRAEAOw=="
              unoptimized
            />
          </Box>
        </MatureContentCover>
      </SEOTagsCover>
    );
  }, [
    imageSrc,
    tags,
    matureContent,
    prefersReducedMotion,
    zoomOnHover,
    altText,
    imagePriority,
    disableSeoTag,
  ]);

  const clickEvent = (event: MouseEvent) => {
    if (onClick) onClick(event);

    if (rsLocation && rsPageType) {
      trackRSProductClicked({
        rsClient: RudderstackClient,
        location: rsLocation,
        pageType: rsPageType,
      });
    }
  };

  return (
    <MediaCard
      onClick={clickEvent}
      data-testid={testId ?? 'related-work-card'}
      onMouseDown={mouseDownEvent}
      transparent
      href={link}
      title={title}
      caption={cardCaption}
      imageRender={ImageElement}
      loading={loading}
      primaryActionSlot={primaryAction}
      hideLink={hideLink}
    >
      {children}
    </MediaCard>
  );
};
